import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid, Autocomplete, TextField, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CrudService from "services/cruds-service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import countryOptions from "../components/List/Countries";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MenuItem from "@mui/material/MenuItem";
import {companies} from "../companies";
import { appAlert } from "components/AppDialog";
import { AddRowButton, RemoveRowButton } from "components/RowActions";

const CreateWeeklyShipping = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const weeklyShippingId = new URLSearchParams(search).get("id");
    const isEditing = Boolean(weeklyShippingId);
    // An existing plan opens read-only, like Create Road Shipment; "Edit" unlocks the form.
    const [readOnlyMode, setReadOnlyMode] = useState(isEditing);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [pickupDate, setPickupDate] = useState(null);
    const [departureDate, setDepartureDate] = useState(null);
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [week, setWeek] = useState("");
    const [serviceProviders, setServiceProviders] = useState([]);
    const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);
    const [shipper, setShipper] = useState("");
    const [consignee, setConsignee] = useState("");
    const [transitTime, setTransitTime] = useState("");
    const [plateNumber, setPlateNumber] = useState("");
    const [tradeTypes, setTradeTypes] = useState([]); // multiple selection
    const [status, setStatus] = useState("");
    const [pickupCountry, setPickupCountry] = useState("TR");
    const [pickupZipcode, setPickupZipcode] = useState("");
    const [pickupAddress, setPickupAddress] = useState("");
    const [deliveryCountry, setDeliveryCountry] = useState("TR");
    const [deliveryZipcode, setDeliveryZipcode] = useState("");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [origin, setOrigin] = useState("");
    const [exitCustoms, setExitCustoms] = useState("");
    const [destinationCustoms, setDestinationCustoms] = useState("");
    const [transportType, setTransportType] = useState("FTL");
    const [ftl, setFtl] = useState({ pcs: "", grossWeight: "", currency: "EUR" });
    const [ltlRows, setLtlRows] = useState([{ pcs: "", chargeableWeight: "", grossWeight: "", ldm: "", cbm: "" }]);
    const [linehaulCost, setLinehaulCost] = useState("");
    const [linehaulCurrency, setLinehaulCurrency] = useState("EUR");
    const [sellingRate, setSellingRate] = useState("");
    const [sellingCurrency, setSellingCurrency] = useState("EUR");
    const [grossProfit, setGrossProfit] = useState(0);
    const [remarks, setRemarks] = useState("");
    const currencyOptions = ["EUR", "USD", "TRY", "GBP"];

    const handleAddLtlRow = () => {
        setLtlRows([...ltlRows, { pcs: "", chargeableWeight: "", grossWeight: "", ldm: "", cbm: "" }]);
    };
    const handleRemoveLtlRow = (index) => {
        const updated = [...ltlRows];
        updated.splice(index, 1);
        setLtlRows(updated);
    };

    const handleLtlChange = (index, field, value) => {
        const updated = [...ltlRows];
        updated[index][field] = value;
        setLtlRows(updated);
    };

    const calculateLtlTotals = () => {
        return ltlRows.reduce(
            (totals, row) => {
                return {
                    pcs: totals.pcs + Number(row.pcs || 0),
                    chargeableWeight: totals.chargeableWeight + Number(row.chargeableWeight || 0),
                    grossWeight: totals.grossWeight + Number(row.grossWeight || 0),
                    ldm: totals.ldm + Number(row.ldm || 0),
                    cbm: totals.cbm + Number(row.cbm || 0),
                };
            },
            { pcs: 0, chargeableWeight: 0, grossWeight: 0, ldm: 0, cbm: 0 }
        );
    };

    const totals = calculateLtlTotals();

    const tradeTypeOptions = ["Export", "Import"];
    const statusOptions = ["Organized", "On the Way", "Delivered"];

    const fetchAddressFromZip = async (country, zipcode, setAddress) => {
        if (!zipcode) return;

        try {
            const response = await fetch(`https://api.zippopotam.us/${country}/${zipcode}`);
            if (!response.ok) throw new Error("No data found");

            const data = await response.json();
            const place = data.places?.[0];
            const fullAddress = `${place["place name"]}, ${place["state"]}, ${country}`;
            setAddress(fullAddress);
        } catch (error) {
            console.error("Posta kodu bulunamadı:", error);
            setAddress("");
        }
    };

    useEffect(() => {
        fetchAddressFromZip(pickupCountry, pickupZipcode, setPickupAddress);
    }, [pickupZipcode, pickupCountry]);

    useEffect(() => {
        fetchAddressFromZip(deliveryCountry, deliveryZipcode, setDeliveryAddress);
    }, [deliveryZipcode, deliveryCountry]);



    // Week calculation based on pickupDate
    useEffect(() => {
        if (pickupDate) {
            const date = new Date(pickupDate);
            const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
            const pastDaysOfYear =
                (date - firstDayOfYear) / 86400000 + firstDayOfYear.getDay();
            const weekNumber = Math.ceil(pastDaysOfYear / 7);
            setWeek(`Week ${weekNumber}`);
        } else {
            setWeek("");
        }
    }, [pickupDate]);

    // Fetch customer options on mount
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await CrudService.getCustomers();

                const options = response.data.map((customer) => {
                    const pickupAddress =
                        customer.attributes?.operationAddresses?.[0]?.pickupAddress || "No Address";

                    return {
                        name: customer.attributes?.name || "Unnamed Customer",
                        id: customer.id,
                        pickupAddress,
                    };
                });

                setCustomerOptions(options);
            } catch (error) {
                console.error("Müşteri verileri alınırken hata oluştu:", error);
            }
        };

        fetchCustomers();
    }, []);

    const [partyOptions, setPartyOptions] = useState([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [customersRes, agentsRes] = await Promise.all([
                    CrudService.getCustomers(),
                    CrudService.getGlobalAgents(),
                ]);

                const customers = (customersRes.data || []).map((c) => ({
                    id: c.id,
                    name: c.attributes?.name || "Unnamed Customer",
                    type: "customer",
                }));

                const agents = (agentsRes.data || []).map((a) => ({
                    id: a.id,
                    name: a.attributes?.name || a.name || "Unnamed Global Agent",
                    type: "agent",
                }));

                const merged = [...customers, ...agents].sort((x, y) =>
                    (x.name || "").localeCompare(y.name || "")
                );

                setCustomerOptions(customers);  // ✅ EKLE
                setPartyOptions(merged);
            } catch (err) {
                console.error("Options alınırken hata oluştu:", err);
            }
        };

        fetchOptions();
    }, []);

// companies (string[]) + partyOptions (object[]) => string[]
    const shipperConsigneeOptions = React.useMemo(() => {
        const companyItems = (companies || [])
            .map((name) => String(name || "").trim())
            .filter(Boolean)
            .map((name) => ({
                id: `company-${name.toLowerCase()}`,
                name,
                type: "company",
            }));

        const partyItems = (partyOptions || [])
            .filter((p) => p?.name)
            .map((p) => ({
                id: `${p.type}-${p.id}`,
                name: String(p.name).trim(),
                type: p.type, // "customer" | "agent"
            }));

        // unique by (type + name) (aynı isim farklı tipte olabilir)
        const map = new Map();
        [...companyItems, ...partyItems].forEach((item) => {
            const key = `${item.type}::${item.name.toLowerCase()}`;
            if (!map.has(key)) map.set(key, item);
        });

        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [partyOptions, companies]);

    const findOptionByName = (name) =>
        shipperConsigneeOptions.find(
            (o) => (o.name || "").toLowerCase() === String(name || "").toLowerCase()
        ) || null;

    useEffect(() => {
        const fetchServiceProviders = async () => {
            try {
                const response = await CrudService.getServiceProviders();
                const options = response.data.map(
                    (item) => item.attributes?.name || "Unnamed Service Provider"
                );
                setServiceProviders(options);
            } catch (error) {
                console.error("Service Provider verileri alınırken hata oluştu:", error);
            }
        };

        fetchServiceProviders();
    }, []);

    const setFormFieldsFromWeeklyShipping = (data) => {
        if (!data) return;
        const matchedCustomer = customerOptions.find(
            (opt) => opt.id === data.customerId || opt.id === data.customerId?._id
        );
        setSelectedCustomer(matchedCustomer || null);
        setPickupDate(data.pickupDate ? dayjs(data.pickupDate) : null);
        setDepartureDate(data.departureDate ? dayjs(data.departureDate) : null);
        setDeliveryDate(data.deliveryDate ? dayjs(data.deliveryDate) : null);
        setWeek(data.week || "");
        setSelectedServiceProvider(
            serviceProviders.find(
                (sp) => sp?.toLowerCase().trim() === data.serviceProvider?.toLowerCase().trim()
            ) || null
        );
        setShipper(data.shipper || "");
        setConsignee(data.consignee || "");
        setTransitTime(data.transitTime || "");
        setPlateNumber(data.plateNumber || "");
        setTradeTypes(data.tradeTypes || []);
        setStatus(data.status || "");
        setPickupCountry(data.pickupCountry || "TR");
        setPickupZipcode(data.pickupZipcode || "");
        setPickupAddress(data.pickupAddress || "");
        setDeliveryCountry(data.deliveryCountry || "TR");
        setDeliveryZipcode(data.deliveryZipcode || "");
        setDeliveryAddress(data.deliveryAddress || "");
        setOrigin(data.origin || "");
        setExitCustoms(data.exitCustoms || "");
        setDestinationCustoms(data.destinationCustoms || "");
        setLinehaulCost(data.financials?.linehaulCost || "");
        setLinehaulCurrency(data.financials?.linehaulCurrency || "EUR");
        setSellingRate(data.financials?.sellingRate || "");
        setSellingCurrency(data.financials?.sellingCurrency || "EUR");
        setGrossProfit(data.financials?.grossProfit || 0);
        setRemarks(data.remarks || "");
        setTransportType((data.roadTransportType || "").toUpperCase());
        if (data.roadTransportType?.toUpperCase() === "FTL") {
            setFtl(data.ftlDetails || { pcs: "", grossWeight: "", currency: "EUR" });
        } else {
            setLtlRows(data.ltlDetails || [
                { pcs: "", chargeableWeight: "", grossWeight: "", ldm: "", cbm: "" }
            ]);
        }
    };

    const [fetchedShippingData, setFetchedShippingData] = useState(null);

    useEffect(() => {
        if (!isEditing) return;
        const fetchData = async () => {
            try {
                const response = await CrudService.getWeeklyShippingById(weeklyShippingId);
                setFetchedShippingData(response.data.attributes);
            } catch (error) {
                console.error("Weekly shipping verisi alınamadı:", error);
            }
        };

        fetchData();
    }, [isEditing, weeklyShippingId]);

    useEffect(() => {
        if (fetchedShippingData && customerOptions.length > 0) {
            setFormFieldsFromWeeklyShipping(fetchedShippingData);
        }
    }, [fetchedShippingData, customerOptions, serviceProviders]);



    const handleSubmit = async () => {
        if (!selectedCustomer) {
            setErrorMessage("Please select a customer before saving.");
            return;
        }
        setErrorMessage("");

        const payload = {
            customerId: selectedCustomer.id,
            pickupDate: dayjs(pickupDate).format("YYYY-MM-DD"),
            departureDate: dayjs(departureDate).format("YYYY-MM-DD"),
            deliveryDate: dayjs(deliveryDate).format("YYYY-MM-DD"),
            week,
            serviceProvider: selectedServiceProvider,
            shipper,
            consignee,
            transitTime,
            plateNumber,
            tradeTypes,
            status,
            pickupCountry,
            pickupAddress,
            deliveryCountry,
            deliveryAddress,
            origin,
            exitCustoms,
            destinationCustoms,
            pickupZipcode,
            deliveryZipcode,
            roadTransportType: transportType,
            ...(transportType === "FTL"
                ? { ftlDetails: ftl }
                : { ltlDetails: ltlRows }),
            financials: {
                linehaulCost,
                linehaulCurrency,
                sellingRate,
                sellingCurrency,
                grossProfit,
            },
            remarks,
        };
        setLoading(true);
        try {
            if (isEditing) {
                await CrudService.updateWeeklyShipping(payload, String(weeklyShippingId));
                setSuccessMessage("Weekly shipping updated successfully.");
            } else {
                await CrudService.createWeeklyShipping({ data: payload });
                setSuccessMessage("Weekly shipping created successfully.");
            }

            setTimeout(() => {
                navigate("/weekly-shipping-order/weekly-shipment-tables/details");
            }, 1500);
        } catch (error) {
            console.error("Submit sırasında hata oluştu:", error);
            appAlert("Gönderim sırasında bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        const totalCost = parseFloat(linehaulCost || 0);
        const profit = parseFloat(sellingRate || 0) - totalCost;
        setGrossProfit(isNaN(profit) ? 0 : profit.toFixed(2));
    }, [ftl, ltlRows, linehaulCost, sellingRate, transportType]);


    const inputStyle = {
        "& .MuiInputBase-root": { height: 40 },
        "& .MuiOutlinedInput-input": { padding: "0 14px" },
        "& .MuiInputLabel-root": { fontSize: 14 },
    };

    const COMMON_INPUT_SX = {
        "& .MuiOutlinedInput-root": { height: 40 },
        "& .MuiInputBase-input": { padding: "8.5px 14px" },
    };

    const SELECT_INPUT_SX = {
        "& .MuiOutlinedInput-root": { height: 40 },
        "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            padding: "0 14px !important",
            height: "40px",
            boxSizing: "border-box",
        },
    };

    const PRICING_INPUT_SX = {
        "& .MuiOutlinedInput-root": {
            height: 60,
            minHeight: 60,
            display: "flex",
            alignItems: "center",
        },
        "& .MuiInputBase-input": {
            height: "60px",
            boxSizing: "border-box",
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
        },
        "& .MuiSelect-select": {
            height: "60px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            padding: "0 14px !important",
        },
    };

    const datePickerSlotProps = {
        textField: {
            variant: "outlined",
            fullWidth: true,
            InputLabelProps: { shrink: true },
            sx: COMMON_INPUT_SX,
        },
        actionBar: { actions: ["clear", "accept"] },
    };

    const partyTypeLabel = (type) =>
        type === "customer" ? "(Customer)" : type === "agent" ? "(Global Agent)" : "(Company)";

    const renderPartyOption = ({ key: optionKey, ...props }, option) => (
        <li {...props} key={option.id}>
            {option.name}
            <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
                {partyTypeLabel(option.type)}
            </span>
        </li>
    );

    const renderCountryOption = ({ key: optionKey, ...props }, option) => (
        <li key={optionKey} {...props} style={{ display: "flex", alignItems: "center" }}>
            {option.flagUrl && (
                <img
                    src={option.flagUrl}
                    alt={`${option.label} flag`}
                    width={20}
                    height={15}
                    style={{ marginRight: 8 }}
                />
            )}
            {option.label}
        </li>
    );

    const renderCurrencyOptions = () =>
        currencyOptions.map((cur) => (
            <MenuItem key={cur} value={cur}>{cur}</MenuItem>
        ));

    const grossProfitColor =
        grossProfit > 0 ? "#027A48" : grossProfit < 0 ? "#B42318" : "#B54708";

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox mb={1}>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} lg={12}>
                            <MDBox mb={2} textAlign="center">
                                <MDBox mb={1} mt={2}>
                                    {successMessage && (
                                        <Alert
                                            severity="success"
                                            variant="filled"
                                            onClose={() => setSuccessMessage("")}
                                            style={{
                                                backgroundColor: "#ECFDF3",
                                                color: "#027A48",
                                                border: "1px solid #ABEFC6",
                                                fontWeight: 600,
                                                padding: "10px",
                                                textAlign: "center",
                                                marginBottom: "10px",
                                                borderRadius: "5px",
                                            }}
                                        >
                                            {successMessage}
                                        </Alert>
                                    )}

                                    {errorMessage && (
                                        <Alert
                                            severity="error"
                                            variant="filled"
                                            onClose={() => setErrorMessage("")}
                                            sx={{ width: "100%", mt: 2, mb: 2 }}
                                        >
                                            {errorMessage}
                                        </Alert>
                                    )}
                                    <MDTypography variant="h3" fontWeight="bold">
                                        {isEditing ? "Edit Weekly Plan Details" : "Add Weekly Plan Details"}
                                    </MDTypography>
                                </MDBox>
                                <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                    This information will describe more about the weekly shipping plan.
                                </MDTypography>
                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>

                {readOnlyMode && (
                    <MDBox mb={2}>
                        <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={2}
                            sx={{
                                p: 2,
                                borderRadius: "10px",
                                background: "#FFF8E1",
                                border: "1px solid #FFD54F",
                            }}
                        >
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                                <LockOutlinedIcon sx={{ color: "#B8860B" }} />
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#7A5B00" }}>
                                    This weekly plan is in read-only view. Click Edit to make changes.
                                </MDTypography>
                            </MDBox>
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => setReadOnlyMode(false)}
                                style={{ backgroundColor: "#B8860B", color: "#FFFFFF" }}
                            >
                                Edit
                            </Button>
                        </MDBox>
                    </MDBox>
                )}

                <fieldset
                    disabled={readOnlyMode}
                    style={{
                        border: "none",
                        padding: 0,
                        margin: 0,
                        opacity: readOnlyMode ? 0.65 : 1,
                        // fieldset[disabled] only locks real form controls; the date
                        // picker buttons and autocomplete popups are cut off here too.
                        pointerEvents: readOnlyMode ? "none" : "auto",
                        transition: "opacity .15s ease",
                    }}
                >
                <MDBox mb={4}
                       sx={{
                           p: 3,
                           borderRadius: "10px",
                           bgcolor: "background.paper",
                           boxShadow: "none",
                       }}>
                    {/* ================= PLAN DETAILS ================= */}
                    <MDBox mt={1}>
                        <MDTypography variant="h6">Weekly Plan Details</MDTypography>
                    </MDBox>

                    <MDBox mt={3}>
                        <Grid container spacing={3}>
                            {/* Selected Customer */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={customerOptions}
                                    value={selectedCustomer || null}
                                    onChange={(event, newValue) => setSelectedCustomer(newValue)}
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Customer"
                                            variant="outlined"
                                            fullWidth
                                            required
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>

                            {/* Service Provider */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={serviceProviders}
                                    value={selectedServiceProvider || null}
                                    onChange={(event, newValue) => setSelectedServiceProvider(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Service Provider"
                                            placeholder="Select Service Provider"
                                            variant="outlined"
                                            fullWidth
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={shipperConsigneeOptions}
                                    value={findOptionByName(shipper)}
                                    onChange={(event, newValue) => setShipper(newValue?.name || "")}
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                    renderOption={renderPartyOption}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Shipper"
                                            fullWidth
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={shipperConsigneeOptions}
                                    value={findOptionByName(consignee)}
                                    onChange={(event, newValue) => setConsignee(newValue?.name || "")}
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                    renderOption={renderPartyOption}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Consignee"
                                            fullWidth
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Transit Time */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Transit Time"
                                    value={transitTime}
                                    onChange={(e) => setTransitTime(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* Plate Number */}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Plate Number"
                                    value={plateNumber}
                                    onChange={(e) => setPlateNumber(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* Trade Types (Multiple Select) */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    multiple
                                    options={tradeTypeOptions}
                                    value={tradeTypes}
                                    onChange={(event, newValue) => setTradeTypes(newValue)}
                                    ChipProps={{ size: "small" }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Trade Types"
                                            variant="outlined"
                                            fullWidth
                                            sx={{ "& .MuiInputBase-root": { minHeight: 40, py: "2px" } }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>

                            {/* Status (Single Select) */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={statusOptions}
                                    value={status || null}
                                    onChange={(event, newValue) => setStatus(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Status"
                                            variant="outlined"
                                            fullWidth
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </MDBox>

                    {/* ================= DATES ================= */}
                    <MDBox mt={4}>
                        <MDTypography variant="h6">Shipment Dates</MDTypography>
                    </MDBox>

                    <MDBox mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label="Pickup Date"
                                    value={pickupDate}
                                    onChange={(newValue) => setPickupDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={datePickerSlotProps}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label="Departure Date"
                                    value={departureDate}
                                    onChange={(newValue) => setDepartureDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={datePickerSlotProps}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label="Delivery Date"
                                    value={deliveryDate}
                                    onChange={(newValue) => setDeliveryDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={datePickerSlotProps}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Week"
                                    value={week}
                                    variant="outlined"
                                    fullWidth
                                    disabled
                                    placeholder="Calculated from pickup date"
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </MDBox>

                    {/* ================= LOCATIONS ================= */}
                    <Grid container spacing={3} mt={1}>
                        {/* Pickup Location */}
                        <Grid item xs={12} md={6}>
                            <MDBox mb={3}>
                                <MDTypography variant="h6">Pickup Location</MDTypography>
                            </MDBox>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={countryOptions}
                                        getOptionLabel={(o) => o.label}
                                        isOptionEqualToValue={(o, v) => o.label === v.label}
                                        value={countryOptions.find((c) => c.code === pickupCountry) || null}
                                        onChange={(e, newValue) => setPickupCountry(newValue?.code || "")}
                                        renderOption={renderCountryOption}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Pickup Country"
                                                placeholder="Select"
                                                fullWidth
                                                sx={inputStyle}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Pickup Zipcode"
                                        value={pickupZipcode}
                                        onChange={(e) => setPickupZipcode(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={COMMON_INPUT_SX}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Pickup Full Address"
                                        value={pickupAddress}
                                        onChange={(e) => setPickupAddress(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={COMMON_INPUT_SX}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Destination Location */}
                        <Grid item xs={12} md={6}>
                            <MDBox mb={3}>
                                <MDTypography variant="h6">Destination Location</MDTypography>
                            </MDBox>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={countryOptions}
                                        getOptionLabel={(o) => o.label}
                                        isOptionEqualToValue={(o, v) => o.label === v.label}
                                        value={countryOptions.find((c) => c.code === deliveryCountry) || null}
                                        onChange={(e, newValue) => setDeliveryCountry(newValue?.code || "")}
                                        renderOption={renderCountryOption}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Delivery Country"
                                                placeholder="Select"
                                                fullWidth
                                                sx={inputStyle}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Delivery Zipcode"
                                        value={deliveryZipcode}
                                        onChange={(e) => setDeliveryZipcode(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={COMMON_INPUT_SX}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Delivery Full Address"
                                        value={deliveryAddress}
                                        onChange={(e) => setDeliveryAddress(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={COMMON_INPUT_SX}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ================= CUSTOMS ================= */}
                    <MDBox mt={4}>
                        <MDTypography variant="h6">Customs Details</MDTypography>
                    </MDBox>

                    <MDBox mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Origin"
                                    value={origin}
                                    onChange={(e) => setOrigin(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Exit Customs Point"
                                    value={exitCustoms}
                                    onChange={(e) => setExitCustoms(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Destination Arrival Customs Point"
                                    value={destinationCustoms}
                                    onChange={(e) => setDestinationCustoms(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={COMMON_INPUT_SX}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </MDBox>

                    {/* ================= CARGO DETAILS ================= */}
                    <MDBox mt={4} display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6">Cargo Details</MDTypography>
                        {transportType !== "FTL" && (
                            <AddRowButton onClick={handleAddLtlRow} label="Add Cargo" />
                        )}
                    </MDBox>

                    <MDBox mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={["FTL", "LTL"]}
                                    value={transportType || null}
                                    onChange={(e, newValue) => setTransportType(newValue)}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Road Transport Type"
                                            placeholder="Select"
                                            fullWidth
                                            sx={inputStyle}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {transportType === "FTL" ? (
                            <MDBox mt={3} p={2} borderRadius={2} border="1px solid #eee">
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="PCS"
                                            value={ftl.pcs}
                                            onChange={(e) => setFtl({ ...ftl, pcs: e.target.value })}
                                            fullWidth
                                            sx={COMMON_INPUT_SX}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            label="Gross Weight"
                                            value={ftl.grossWeight}
                                            onChange={(e) => setFtl({ ...ftl, grossWeight: e.target.value })}
                                            fullWidth
                                            sx={COMMON_INPUT_SX}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField
                                            select
                                            label="Currency"
                                            value={ftl.currency}
                                            onChange={(e) => setFtl({ ...ftl, currency: e.target.value })}
                                            fullWidth
                                            sx={SELECT_INPUT_SX}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {renderCurrencyOptions()}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        ) : (
                            <>
                                {ltlRows.map((row, index) => (
                                    <MDBox key={index} mt={3} p={2} borderRadius={2} border="1px solid #eee">
                                        <Grid container spacing={3} alignItems="center">
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField label="PCS" value={row.pcs} onChange={(e) => handleLtlChange(index, "pcs", e.target.value)} fullWidth sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2.5}>
                                                <TextField label="Chargeable Weight" value={row.chargeableWeight} onChange={(e) => handleLtlChange(index, "chargeableWeight", e.target.value)} fullWidth sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2.5}>
                                                <TextField label="Gross Weight" value={row.grossWeight} onChange={(e) => handleLtlChange(index, "grossWeight", e.target.value)} fullWidth sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField label="LDM" value={row.ldm} onChange={(e) => handleLtlChange(index, "ldm", e.target.value)} fullWidth sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField label="CBM" value={row.cbm} onChange={(e) => handleLtlChange(index, "cbm", e.target.value)} fullWidth sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={1} display="flex" justifyContent="flex-end">
                                                <RemoveRowButton onClick={() => handleRemoveLtlRow(index)} tooltip="Remove cargo" />
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                ))}

                                {ltlRows.length > 1 && (
                                    <>
                                        <MDBox mt={3}>
                                            <MDTypography variant="h6">Total Values</MDTypography>
                                        </MDBox>
                                        <Grid container spacing={3} mt={0}>
                                            <Grid item xs={12} sm={6} md={2}><TextField label="Total PCS" value={totals.pcs} fullWidth disabled sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} /></Grid>
                                            <Grid item xs={12} sm={6} md={2.5}><TextField label="Total Chargeable Weight" value={totals.chargeableWeight} fullWidth disabled sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} /></Grid>
                                            <Grid item xs={12} sm={6} md={2.5}><TextField label="Total Gross Weight" value={totals.grossWeight} fullWidth disabled sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} /></Grid>
                                            <Grid item xs={12} sm={6} md={2}><TextField label="Total LDM" value={totals.ldm} fullWidth disabled sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} /></Grid>
                                            <Grid item xs={12} sm={6} md={2}><TextField label="Total CBM" value={totals.cbm} fullWidth disabled sx={COMMON_INPUT_SX} InputLabelProps={{ shrink: true }} /></Grid>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </MDBox>

                    {/* ================= PRICING DETAILS ================= */}
                    <MDBox mt={4}>
                        <MDTypography variant="h6">Pricing Details</MDTypography>
                    </MDBox>

                    <Grid container spacing={2} mt={1} alignItems="center">
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <TextField
                                label={transportType === "FTL" ? "Freight Charge" : "Linehaul Cost"}
                                placeholder={transportType === "FTL" ? "Enter Freight Charge" : "Enter Linehaul Cost"}
                                value={linehaulCost}
                                onChange={(e) => setLinehaulCost(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={PRICING_INPUT_SX}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TextField
                                select
                                label="Currency"
                                value={linehaulCurrency}
                                onChange={(e) => setLinehaulCurrency(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={PRICING_INPUT_SX}
                            >
                                {renderCurrencyOptions()}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <TextField
                                label="Selling Rate"
                                placeholder="Enter Selling Rate"
                                value={sellingRate}
                                onChange={(e) => setSellingRate(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={PRICING_INPUT_SX}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TextField
                                select
                                label="Currency"
                                value={sellingCurrency}
                                onChange={(e) => setSellingCurrency(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={PRICING_INPUT_SX}
                            >
                                {renderCurrencyOptions()}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} lg={2}>
                            <MDBox
                                sx={{
                                    height: 60,
                                    px: 2,
                                    borderRadius: "8px",
                                    border: "1px solid #eee",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}
                            >
                                <MDTypography variant="caption" color="secondary" noWrap>
                                    Gross Profit
                                </MDTypography>
                                <MDTypography variant="h6" noWrap sx={{ color: `${grossProfitColor} !important` }}>
                                    {grossProfit} {sellingCurrency}
                                </MDTypography>
                            </MDBox>
                        </Grid>
                    </Grid>

                    {/* ================= REMARKS ================= */}
                    <MDBox mt={4}>
                        <MDTypography variant="h6">Remarks</MDTypography>
                    </MDBox>

                    <Grid container spacing={3} mt={0}>
                        <Grid item xs={12}>
                            <TextField
                                label="Remarks"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </MDBox>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "24px" }}>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: "#0B1F3A", color: "#FFFFFF" }}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : isEditing ? "Update Weekly Plan" : "Create Weekly Plan"}
                    </Button>
                </div>
                </fieldset>
                <Footer />
            </DashboardLayout>
        </LocalizationProvider>
    );
};

export default CreateWeeklyShipping;
